import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const VersionHistory = ({ 
  versions = [], 
  currentVersion = null,
  onRestoreVersion,
  onDeleteVersion,
  onCompareVersions 
}) => {
  const [selectedVersions, setSelectedVersions] = useState([]);
  const [expandedVersion, setExpandedVersion] = useState(null);

  const handleVersionSelect = (versionId) => {
    if (selectedVersions?.includes(versionId)) {
      setSelectedVersions(selectedVersions?.filter(id => id !== versionId));
    } else if (selectedVersions?.length < 2) {
      setSelectedVersions([...selectedVersions, versionId]);
    }
  };

  const handleCompare = () => {
    if (selectedVersions?.length === 2) {
      onCompareVersions(selectedVersions?.[0], selectedVersions?.[1]);
    }
  };

  const formatDate = (date) => {
    return new Date(date)?.toLocaleString('en-IN', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getVersionIcon = (version) => {
    if (version?.id === currentVersion?.id) return 'CheckCircle';
    if (version?.isAutoSave) return 'Save';
    return 'FileText';
  };

  const getVersionColor = (version) => {
    if (version?.id === currentVersion?.id) return 'text-success';
    if (version?.isAutoSave) return 'text-primary';
    return 'text-muted-foreground';
  };

  return (
    <div className="bg-card rounded-lg border border-border overflow-hidden">
      {/* Header */}
      <div className="p-4 border-b border-border bg-muted/30">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center space-x-2">
            <Icon name="History" size={20} className="text-primary" />
            <h3 className="font-semibold text-card-foreground">Version History</h3>
            <span className="px-2 py-1 bg-primary/10 text-primary text-xs rounded-full">
              {versions?.length}
            </span>
          </div>
          <div className="flex items-center space-x-2">
            {selectedVersions?.length === 2 && (
              <Button 
                variant="outline" 
                size="sm" 
                iconName="GitCompare"
                onClick={handleCompare}
              >
                Compare
              </Button>
            )}
            <Button 
              variant="outline" 
              size="sm" 
              iconName="Download"
            >
              Export All
            </Button>
          </div>
        </div>

        {selectedVersions?.length > 0 && (
          <div className="bg-primary/5 border border-primary/20 rounded-md p-3">
            <div className="flex items-center justify-between">
              <div className="text-sm text-primary">
                {selectedVersions?.length} version{selectedVersions?.length > 1 ? 's' : ''} selected
              </div>
              <button
                onClick={() => setSelectedVersions([])}
                className="text-xs text-muted-foreground hover:text-foreground"
              >
                Clear selection
              </button>
            </div>
          </div>
        )}
      </div>
      {/* Versions List */}
      <div className="max-h-96 overflow-y-auto">
        {versions?.length === 0 ? (
          <div className="flex items-center justify-center py-12">
            <div className="text-center">
              <Icon name="History" size={32} className="text-muted-foreground mx-auto mb-3" />
              <p className="text-sm text-muted-foreground">No version history available</p>
              <p className="text-xs text-muted-foreground mt-1">
                Versions will be saved automatically as you make changes
              </p>
            </div>
          </div>
        ) : (
          <div className="space-y-2 p-4">
            {versions?.map((version) => (
              <div 
                key={version?.id}
                className={`border rounded-lg transition-all duration-200 ${
                  selectedVersions?.includes(version?.id) 
                    ? 'border-primary bg-primary/5' :'border-border hover:bg-muted/30'
                } ${version?.id === currentVersion?.id ? 'ring-2 ring-success/20' : ''}`}
              >
                <div className="p-4">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-3 flex-1">
                      {/* Selection Checkbox */}
                      <button
                        onClick={() => handleVersionSelect(version?.id)}
                        className={`w-4 h-4 rounded border-2 flex items-center justify-center transition-colors duration-200 ${
                          selectedVersions?.includes(version?.id)
                            ? 'bg-primary border-primary' :'border-muted-foreground hover:border-primary'
                        }`}
                      >
                        {selectedVersions?.includes(version?.id) && (
                          <Icon name="Check" size={12} color="white" />
                        )}
                      </button>

                      {/* Version Icon */}
                      <Icon 
                        name={getVersionIcon(version)} 
                        size={16} 
                        className={getVersionColor(version)} 
                      />

                      {/* Version Info */}
                      <div className="flex-1">
                        <div className="flex items-center space-x-2">
                          <span className="font-medium text-card-foreground text-sm">
                            {version?.name}
                          </span>
                          {version?.id === currentVersion?.id && (
                            <span className="px-2 py-0.5 bg-success/10 text-success text-xs rounded-full">
                              Current
                            </span>
                          )}
                          {version?.isAutoSave && (
                            <span className="px-2 py-0.5 bg-primary/10 text-primary text-xs rounded-full">
                              Auto-saved
                            </span>
                          )}
                        </div>
                        <div className="text-xs text-muted-foreground mt-1">
                          {formatDate(version?.createdAt)} • {version?.changes} changes
                        </div>
                      </div>
                    </div>

                    {/* Actions */}
                    <div className="flex items-center space-x-2">
                      <div className="text-xs text-muted-foreground">
                        {version?.matchScore}% match
                      </div>
                      <button
                        onClick={() => setExpandedVersion(
                          expandedVersion === version?.id ? null : version?.id
                        )}
                        className="p-1 hover:bg-muted rounded-md transition-colors duration-200"
                      >
                        <Icon 
                          name={expandedVersion === version?.id ? "ChevronUp" : "ChevronDown"} 
                          size={16} 
                          className="text-muted-foreground" 
                        />
                      </button>
                    </div>
                  </div>

                  {/* Expanded Content */}
                  {expandedVersion === version?.id && (
                    <div className="mt-4 pt-4 border-t border-border space-y-3">
                      {/* Version Description */}
                      {version?.description && (
                        <p className="text-sm text-muted-foreground">
                          {version?.description}
                        </p>
                      )}

                      {/* Changes Summary */}
                      <div className="space-y-2">
                        <div className="text-xs font-medium text-card-foreground">Changes Made:</div>
                        <div className="space-y-1">
                          {version?.changesSummary?.map((change, index) => (
                            <div key={index} className="flex items-center space-x-2 text-xs">
                              <Icon 
                                name={change?.type === 'added' ? 'Plus' : change?.type === 'removed' ? 'Minus' : 'Edit'} 
                                size={12} 
                                className={
                                  change?.type === 'added' ? 'text-success' : 
                                  change?.type === 'removed' ? 'text-error' : 'text-warning'
                                } 
                              />
                              <span className="text-muted-foreground">{change?.description}</span>
                            </div>
                          ))}
                        </div>
                      </div>

                      {/* Action Buttons */}
                      <div className="flex items-center space-x-2 pt-2">
                        {version?.id !== currentVersion?.id && (
                          <Button
                            variant="outline"
                            size="sm"
                            iconName="RotateCcw"
                            onClick={() => onRestoreVersion(version?.id)}
                          >
                            Restore
                          </Button>
                        )}
                        <Button
                          variant="ghost"
                          size="sm"
                          iconName="Download"
                        >
                          Download
                        </Button>
                        {!version?.isAutoSave && version?.id !== currentVersion?.id && (
                          <Button
                            variant="ghost"
                            size="sm"
                            iconName="Trash2"
                            onClick={() => onDeleteVersion(version?.id)}
                            className="text-error hover:text-error"
                          >
                            Delete
                          </Button>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
      {/* Footer */}
      <div className="p-4 border-t border-border bg-muted/30">
        <div className="flex items-center justify-between text-sm">
          <div className="text-muted-foreground">
            Auto-save every 30 seconds
          </div>
          <div className="flex items-center space-x-2">
            <Icon name="Shield" size={16} className="text-success" />
            <span className="text-success">All versions encrypted</span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default VersionHistory;